import React, { useState, useMemo } from 'react';

import { useTheme } from '@mui/material/styles';
import styled, { ThemeProvider } from 'styled-components';

import { useDutchieIntelligenceContext } from '../../context/dutchie-intelligence';

import type { ContextEntry } from '../../context/types';

/**
 * ContextDebugViewer - Shows all registered contexts for debugging.
 * Displays context metadata, size, and allows inspecting the actual data.
 */
export function ResourceContextDebugViewer() {
  const muiTheme = useTheme();
  const { getContextMetadata, getCacheStats, currentPage } = useDutchieIntelligenceContext();
  const [selectedContextId, setSelectedContextId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterSource, setFilterSource] = useState<string>('all');
  const [filterScope, setFilterScope] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'all' | 'individual'>('individual');

  const allContexts = getContextMetadata();
  const cacheStats = getCacheStats();

  // Get unique sources and scopes for filters
  const { sources, scopes } = useMemo(() => {
    const sourcesSet = new Set<string>();
    const scopesSet = new Set<string>();

    Object.values(allContexts).forEach((entry) => {
      if (entry.metadata.source) {
        sourcesSet.add(entry.metadata.source);
      }
      if (entry.metadata.scope) {
        scopesSet.add(entry.metadata.scope);
      }
    });

    return {
      sources: Array.from(sourcesSet).sort(),
      scopes: Array.from(scopesSet).sort(),
    };
  }, [allContexts]);

  // Filter contexts based on search and filters
  const filteredContexts = useMemo(
    () =>
      Object.entries(allContexts).filter(([id, entry]) => {
        // Search filter
        if (searchTerm) {
          const searchLower = searchTerm.toLowerCase();
          const matchesId = id.toLowerCase().includes(searchLower);
          const matchesScope = entry.metadata.scope?.toLowerCase().includes(searchLower);
          const matchesType = entry.metadata.type?.toLowerCase().includes(searchLower);
          if (!matchesId && !matchesScope && !matchesType) {
            return false;
          }
        }

        // Source filter
        if (filterSource !== 'all' && entry.metadata.source !== filterSource) {
          return false;
        }

        // Scope filter
        if (filterScope !== 'all' && entry.metadata.scope !== filterScope) {
          return false;
        }

        return true;
      }),
    [allContexts, searchTerm, filterSource, filterScope]
  );

  // Sort contexts by timestamp (newest first)
  const sortedContexts = useMemo(
    () => filteredContexts.sort((a, b) => b[1].metadata.timestamp - a[1].metadata.timestamp),
    [filteredContexts]
  );

  const selectedContext = selectedContextId ? allContexts[selectedContextId] : null;

  const formatSize = (bytes: number) => {
    if (bytes < 1024) {
      return `${bytes}B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)}KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const formatAge = (timestamp: number) => {
    const ageMs = Date.now() - timestamp;
    if (ageMs < 1000) {
      return 'just now';
    }
    if (ageMs < 60000) {
      return `${Math.floor(ageMs / 1000)}s ago`;
    }
    if (ageMs < 3600000) {
      return `${Math.floor(ageMs / 60000)}m ago`;
    }
    return `${Math.floor(ageMs / 3600000)}h ago`;
  };

  const parseContext = (entry: ContextEntry) => {
    try {
      return JSON.parse(entry.context);
    } catch {
      return entry.context;
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <Container>
        <Header>
          <Title>Resource context debug viewer</Title>
          <Stats>
            <StatItem>
              <StatLabel>Total resources:</StatLabel>
              <StatValue>{Object.keys(allContexts).length}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Total size:</StatLabel>
              <StatValue>{formatSize(cacheStats.totalSize)}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Pages:</StatLabel>
              <StatValue>{cacheStats.pageCount}</StatValue>
            </StatItem>
            <StatItem>
              <StatLabel>Current page:</StatLabel>
              <StatValue>{currentPage || 'None'}</StatValue>
            </StatItem>
          </Stats>
        </Header>

        <Filters>
          <SearchInput
            placeholder='Search by ID, scope, or type...'
            type='text'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <FilterSelect value={filterSource} onChange={(e) => setFilterSource(e.target.value)}>
            <option value='all'>All Sources</option>
            {sources.map((source) => (
              <option key={source} value={source}>
                {source}
              </option>
            ))}
          </FilterSelect>
          <FilterSelect value={filterScope} onChange={(e) => setFilterScope(e.target.value)}>
            <option value='all'>All Scopes</option>
            {scopes.map((scope) => (
              <option key={scope} value={scope}>
                {scope}
              </option>
            ))}
          </FilterSelect>
        </Filters>

        <TabContainer>
          <Tab $isActive={activeTab === 'individual'} onClick={() => setActiveTab('individual')}>
            Individual
          </Tab>
          <Tab $isActive={activeTab === 'all'} onClick={() => setActiveTab('all')}>
            All resources
          </Tab>
        </TabContainer>

        <Content>
          {activeTab === 'individual' ? (
            <>
              <ContextList>
                <ListHeader>
                  <span>Resources ({sortedContexts.length})</span>
                </ListHeader>
                {sortedContexts.map(([id, entry]) => (
                  <ContextItem $isSelected={selectedContextId === id} key={id} onClick={() => setSelectedContextId(id)}>
                    <ContextItemHeader>
                      <ContextId>{id}</ContextId>
                      <ContextBadges>
                        {entry.metadata.global && <Badge $color='#4caf50'>Global</Badge>}
                        {entry.metadata.referencedByAI && <Badge $color='#2196f3'>Referenced</Badge>}
                        <Badge $color='#9e9e9e'>{entry.metadata.priority || 'medium'}</Badge>
                      </ContextBadges>
                    </ContextItemHeader>
                    <ContextItemMeta>
                      <MetaItem>
                        <MetaLabel>Source:</MetaLabel> {entry.metadata.source || 'unknown'}
                      </MetaItem>
                      {entry.metadata.scope && (
                        <MetaItem>
                          <MetaLabel>Scope:</MetaLabel> {entry.metadata.scope}
                        </MetaItem>
                      )}
                      <MetaItem>
                        <MetaLabel>Size:</MetaLabel> {formatSize(entry.metadata.size)}
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Age:</MetaLabel> {formatAge(entry.metadata.timestamp)}
                      </MetaItem>
                    </ContextItemMeta>
                  </ContextItem>
                ))}
                {sortedContexts.length === 0 && <EmptyState>No contexts found matching your filters</EmptyState>}
              </ContextList>

              <DetailPanel>
                {selectedContext ? (
                  <>
                    <DetailHeader>
                      <DetailTitle>{selectedContextId}</DetailTitle>
                    </DetailHeader>
                    <DetailSection>
                      <SectionTitle>Metadata</SectionTitle>
                      <MetadataGrid>
                        <MetadataRow>
                          <MetadataKey>Source:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.source || 'unknown'}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Scope:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.scope || 'none'}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Type:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.type || 'other'}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Priority:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.priority || 'medium'}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Size:</MetadataKey>
                          <MetadataValue>{formatSize(selectedContext.metadata.size)}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Timestamp:</MetadataKey>
                          <MetadataValue>{formatTimestamp(selectedContext.metadata.timestamp)}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Page:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.page || 'none'}</MetadataValue>
                        </MetadataRow>
                        <MetadataRow>
                          <MetadataKey>Global:</MetadataKey>
                          <MetadataValue>{selectedContext.metadata.global ? 'Yes' : 'No'}</MetadataValue>
                        </MetadataRow>
                        {selectedContext.metadata.referencedByAI && (
                          <>
                            <MetadataRow>
                              <MetadataKey>Reference Count:</MetadataKey>
                              <MetadataValue>{selectedContext.metadata.referenceCount || 0}</MetadataValue>
                            </MetadataRow>
                            <MetadataRow>
                              <MetadataKey>Last Referenced:</MetadataKey>
                              <MetadataValue>
                                {selectedContext.metadata.lastReferenced
                                  ? formatTimestamp(selectedContext.metadata.lastReferenced)
                                  : 'Never'}
                              </MetadataValue>
                            </MetadataRow>
                          </>
                        )}
                      </MetadataGrid>
                    </DetailSection>
                    <DetailSection>
                      <SectionTitle>Context Data</SectionTitle>
                      <CodeBlock>
                        <pre>{JSON.stringify(parseContext(selectedContext), null, 2)}</pre>
                      </CodeBlock>
                    </DetailSection>
                  </>
                ) : (
                  <EmptyDetailState>
                    <p>Select a context to view details</p>
                  </EmptyDetailState>
                )}
              </DetailPanel>
            </>
          ) : (
            <AllContextPanel>
              <AllContextHeader>All resource data</AllContextHeader>
              <AllContextCodeBlock>
                <pre>
                  {JSON.stringify(
                    Object.entries(allContexts).reduce<Record<string, any>>((acc, [id, entry]) => {
                      acc[id] = parseContext(entry);
                      return acc;
                    }, {}),
                    null,
                    2
                  )}
                </pre>
              </AllContextCodeBlock>
            </AllContextPanel>
          )}
        </Content>
      </Container>
    </ThemeProvider>
  );
}

// Styled Components

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: ${(props) => props.theme.palette.background.paper};
  color: ${(props) => props.theme.palette.text.primary};
`;

const Header = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const Title = styled.h2`
  margin: 0 0 12px 0;
  font-size: 20px;
  font-weight: 600;
`;

const Stats = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
`;

const StatItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.span`
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 14px;
`;

const StatValue = styled.span`
  font-weight: 600;
  font-size: 14px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 4px;
  padding: 0 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const Tab = styled.button<{ $isActive: boolean }>`
  padding: 12px 20px;
  background: ${(props) => (props.$isActive ? props.theme.palette.action.selected : 'transparent')};
  border: none;
  border-bottom: 2px solid ${(props) => (props.$isActive ? props.theme.palette.primary.main : 'transparent')};
  color: ${(props) => (props.$isActive ? props.theme.palette.text.primary : props.theme.palette.text.secondary)};
  font-weight: ${(props) => (props.$isActive ? 600 : 400)};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => props.theme.palette.action.hover};
  }
`;

const Filters = styled.div`
  display: flex;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  font-size: 14px;
  background: ${(props) => props.theme.palette.background.default};
  color: ${(props) => props.theme.palette.text.primary};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.palette.primary.main};
  }
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  font-size: 14px;
  background: ${(props) => props.theme.palette.background.default};
  color: ${(props) => props.theme.palette.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.palette.primary.main};
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const ContextList = styled.div`
  width: 400px;
  border-right: 1px solid ${(props) => props.theme.palette.divider};
  overflow-y: auto;
`;

const ListHeader = styled.div`
  padding: 12px 16px;
  background: ${(props) => props.theme.palette.background.default};
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  font-weight: 600;
  font-size: 14px;
  position: sticky;
  top: 0;
  z-index: 1;
`;

const ContextItem = styled.div<{ $isSelected: boolean }>`
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  background: ${(props) => (props.$isSelected ? props.theme.palette.action.selected : 'transparent')};

  &:hover {
    background: ${(props) => props.theme.palette.action.hover};
  }
`;

const ContextItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const ContextId = styled.div`
  font-weight: 500;
  font-size: 14px;
  word-break: break-word;
`;

const ContextBadges = styled.div`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
`;

const Badge = styled.span<{ $color: string }>`
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 500;
  background: ${(props) => props.$color};
  color: white;
`;

const ContextItemMeta = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  font-size: 12px;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const MetaItem = styled.div``;

const MetaLabel = styled.span`
  font-weight: 500;
`;

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const DetailPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

const DetailHeader = styled.div`
  margin-bottom: 24px;
`;

const DetailTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  word-break: break-word;
`;

const DetailSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
`;

const MetadataGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const MetadataRow = styled.div`
  display: flex;
  gap: 12px;
  font-size: 14px;
`;

const MetadataKey = styled.div`
  min-width: 150px;
  font-weight: 500;
  color: ${(props) => props.theme.palette.text.secondary};
`;

const MetadataValue = styled.div`
  color: ${(props) => props.theme.palette.text.primary};
`;

const CodeBlock = styled.div`
  background: ${(props) => props.theme.palette.background.default};
  border: 1px solid ${(props) => props.theme.palette.divider};
  border-radius: 4px;
  padding: 16px;
  overflow-x: auto;

  pre {
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    line-height: 1.5;
  }
`;

const EmptyDetailState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${(props) => props.theme.palette.text.secondary};
  font-size: 16px;
`;

const AllContextPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  width: 100%;
`;

const AllContextHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid ${(props) => props.theme.palette.divider};
  font-size: 16px;
  font-weight: 600;
`;

const AllContextCodeBlock = styled.div`
  flex: 1;
  overflow: auto;
  background: ${(props) => props.theme.palette.background.default};
  padding: 16px;

  pre {
    margin: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
    line-height: 1.5;
    color: ${(props) => props.theme.palette.text.primary};
  }
`;
